import { Avatar, Button, Space, Tooltip, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { formatCurrency, type CatalogoRef, type CorteMoraClienteRow } from '@aid/shared';
import { Link } from 'react-router-dom';
import { EstadoClienteTag } from '../../../clientes/components/EstadoClienteTag';
import { StatusTag } from '../../../../components/StatusTag';
import { formatCompactDate, formatearPeriodoCompacto, parseDateOnly } from '../../../../lib/date';
import { tonoEstadoServicio } from '../../../../lib/estado-servicio-tono';
import { i18nInstance } from '../../../../locales';
import { getFacturaInitials } from '../../../cobros/lib/factura-table-helpers';
import { serverSortProps, type ServerSortState } from '../../../../lib/server-sort';

const { Text } = Typography;
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface CorteMoraTableColumnsOptions {
  t: Translate;
  canReadClient: boolean;
  primaryColor: string;
  linkColor?: string;
  secondaryColor?: string;
  sort?: ServerSortState;
}

export function createCorteMoraTableColumns({
  t,
  canReadClient,
  primaryColor,
  linkColor = primaryColor,
  secondaryColor,
  sort = {},
}: CorteMoraTableColumnsOptions): ColumnsType<CorteMoraClienteRow> {
  const dateLocale = i18nInstance.language;
  return [
    {
      title: t('pages.corteMora.client'),
      key: 'cliente',
      ...serverSortProps('cliente', sort),
      width: 240,
      render: (_value: unknown, record: CorteMoraClienteRow) => {
        const name =
          record.cliente.nombre?.trim() || t('pages.corteMora.clientId', { id: record.idCliente });
        const clientPath = `/clientes/${record.idCliente}/facturas`;
        const nameContent = canReadClient ? (
          <Link
            to={clientPath}
            aria-label={t('pages.corteMora.viewClientInvoices', { name })}
            style={{ color: linkColor }}
          >
            {name}
          </Link>
        ) : (
          <Text strong>{name}</Text>
        );
        const secondary = [
          t('pages.corteMora.clientId', { id: record.idCliente }),
          record.cliente.ip
            ? t('pages.corteMora.aidCode', { code: record.cliente.ip })
            : record.cliente.identificacion,
        ]
          .filter((value): value is string => Boolean(value))
          .join(' · ');

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Avatar
              size={30}
              style={{ background: primaryColor, fontSize: 12, flex: '0 0 auto' }}
              aria-hidden
            >
              {getFacturaInitials(record.cliente.nombre)}
            </Avatar>
            <div style={{ minWidth: 0, lineHeight: 1.3 }}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nameContent}
              </div>
              <Text type="secondary" style={{ fontSize: 12, color: secondaryColor }}>
                {secondary}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: t('pages.corteMora.clientStatus'),
      key: 'estadoCliente',
      width: 120,
      render: (_value: unknown, record: CorteMoraClienteRow) => (
        <EstadoClienteTag estado={record.estadoCliente} />
      ),
    },
    {
      title: t('pages.corteMora.serviceStatus'),
      key: 'estadosServicio',
      width: 190,
      render: (_value: unknown, record: CorteMoraClienteRow) =>
        record.estadosServicio.length > 0 ? (
          <Space wrap size={[4, 4]}>
            {record.estadosServicio.map((estado) => (
              <StatusTag key={estado.id} tone={tonoEstadoServicio(estado.key)}>
                {catalogLabel(estado)}
              </StatusTag>
            ))}
          </Space>
        ) : (
          <Text type="secondary" style={{ color: secondaryColor }}>
            {t('pages.corteMora.noServiceStatus')}
          </Text>
        ),
    },
    {
      title: t('pages.corteMora.pendingInvoices'),
      key: 'facturasPendientes',
      ...serverSortProps('facturasPendientes', sort),
      width: 100,
      align: 'right',
      render: (_value: unknown, record: CorteMoraClienteRow) => (
        <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {record.facturasPendientes}
        </span>
      ),
    },
    {
      title: t('pages.corteMora.pendingDebt'),
      key: 'deudaPendiente',
      ...serverSortProps('deudaPendiente', sort),
      width: 130,
      align: 'right',
      render: (_value: unknown, record: CorteMoraClienteRow) => (
        <span
          style={{
            display: 'block',
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {formatCurrency(record.deudaPendiente)}
        </span>
      ),
    },
    {
      title: t('pages.corteMora.oldestInvoice'),
      key: 'facturaMasAntigua',
      ...serverSortProps('facturaMasAntigua', sort),
      width: 170,
      render: (_value: unknown, record: CorteMoraClienteRow) => {
        const compactDate = formatCompactDate(record.facturaMasAntigua, dateLocale);
        const parsed = parseDateOnly(record.facturaMasAntigua);
        const period = formatearPeriodoCompacto(
          record.facturaMasAntiguaPeriodoInicio,
          record.facturaMasAntiguaPeriodoFin,
          dateLocale,
        );
        return (
          <div title={t('pages.corteMora.oldestInvoiceHelp')} style={{ lineHeight: 1.35 }}>
            {compactDate && parsed?.isValid() ? (
              <time dateTime={parsed.format('YYYY-MM-DD')}>{compactDate}</time>
            ) : (
              <Text type="secondary">—</Text>
            )}
            <Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, color: secondaryColor }}
            >
              {period ?? t('pages.corteMora.noPeriod')}
            </Text>
          </div>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 105,
      render: (_value: unknown, record: CorteMoraClienteRow) => {
        const label = t('pages.corteMora.review');
        if (!canReadClient) {
          return (
            <Tooltip title={t('pages.corteMora.clientPermissionRequired')}>
              <Button type="link" size="small" disabled aria-label={label}>
                <EyeOutlined aria-hidden />
                {label}
              </Button>
            </Tooltip>
          );
        }
        return (
          <Link
            to={`/clientes/${record.idCliente}/facturas`}
            aria-label={t('pages.corteMora.reviewClient', {
              name: record.cliente.nombre ?? record.idCliente,
            })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: linkColor }}
          >
            <EyeOutlined aria-hidden />
            {label}
          </Link>
        );
      },
    },
  ];
}

function catalogLabel(catalogo: CatalogoRef): string {
  return catalogo.value ?? catalogo.key ?? '—';
}
